/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import PropTypes from "prop-types"; // Npm: react.js library.
import Moment from "moment"; // Npm: moment.js library.
import _ from "underscore"; // Npm: underscore library.
import { toast } from "react-hot-toast"; // Npm: React hot toast.
import { connect } from "react-redux"; // Npm: React Redux for state management.
import { useMutation, useQuery } from "@apollo/client"; // Npm: Apollo client.
import { IoMdAdd } from "react-icons/io"; // Npm: React icons.
import { HiArrowPath } from "react-icons/hi2"; // Npm: React icons.
import { ChevronRightIcon, ChevronDownIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
  IconButton,
  Box,
} from "@chakra-ui/react";
import { DataGrid } from "@mui/x-data-grid";

/*
 * PACKAGES
 */
import DownloadToExcel from "components/DownloadToExcel";
import CustomerBlockBetweenDuration from "components/CustomerBlockBetweenDuration";
import Modal from "components/Modal";
import CustomerAccountUpsert from "components/CustomerAccountUpsert";
import CustomerAccountDelete from "components/CustomerAccountDelete";
import CustomerAccountDropDownOptions from "components/CustomerAccountDropDownOptions";
import TableSpinner from "components/TableSpinner";

/*
 * GRAPHS
 */
import CustomerAccountReadQuery from "./__query__/index.customerAccount.read.query";
import CustomerRateEmailMutation from "./__mutation__/index.rate.email.mutation";

/*
 * STYLES
 */
import { buttonStyle } from "./index.style";

/*
 * Component
 */
const StatusBadge = React.memo(({ isActive }) =>
  isActive ? (
    <Text
      margin="auto"
      width="max-content"
      borderRadius="10px"
      padding="5px 15px"
      lineHeight="18px"
      color="rgba(117, 81, 255, 1)"
      bg="rgba(117, 81, 255, .1)"
    >
      Active
    </Text>
  ) : (
    <Text
      margin="auto"
      width="max-content"
      borderRadius="10px"
      lineHeight="18px"
      padding="5px 15px"
      color="rgba(238, 93, 80, 1)"
      bg="rgba(255, 242, 242, 1)"
    >
      Inactive
    </Text>
  ),
);

const SendRatesButton = React.memo(({ isInProgress, onClick }) => (
  <Button
    fontSize="15px"
    fontWeight={500}
    py="3px"
    minH="unset"
    h="unset"
    width="max-content"
    margin="auto"
    borderRadius="10px"
    bg={isInProgress ? "purple.100" : "purple.50"}
    _focus={{ bg: "purple.100" }}
    _active={{ bg: "purple.100" }}
    _hover={{ bg: "purple.100" }}
    color="purple.600"
    onClick={onClick}
  >
    Send Rates
  </Button>
));

/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Const assignment
  const _successFlags = React.useMemo(
    () =>
      Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
        (i) => i.key,
      ),
    [],
  );

  // Hook assignment
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 30,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const [sortModel, setSortModel] = React.useState([]);
  const [customerRateEmailInProgress, setCustomerRateEmailInProgress] =
    React.useState([]);
  const [expandedRows, setExpandedRows] = React.useState({});
  const [deletedCustomerAccountIds, setDeletedCustomerAccountIds] =
    React.useState([]);

  const [MutationRateEmail] = useMutation(CustomerRateEmailMutation);

  const {
    onOpen: onCustomerBlockDurationOpen,
    isOpen: isCustomerBlockDurationOpen,
    onClose: onCustomerBlockDurationClose,
  } = useDisclosure();
  const {
    onOpen: onCustomerAccountCreateOpen,
    isOpen: isCustomerAccountCreateOpen,
    onClose: onCustomerAccountCreateClose,
  } = useDisclosure();
  const {
    onOpen: onCustomerAccountUpdateOpen,
    isOpen: isCustomerAccountUpdateOpen,
    onClose: onCustomerAccountUpdateClose,
  } = useDisclosure();
  const {
    onOpen: onCustomerAccountDeleteOpen,
    isOpen: isCustomerAccountDeleteOpen,
    onClose: onCustomerAccountDeleteClose,
  } = useDisclosure();

  const _isFilterApplied = filterModel?.items?.some(
    (i) => i.value != void 0 && i.value !== "",
  );
  const _appliedFilter = _isFilterApplied ? filterModel : void 0;
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);
  const _rowCountRef = React.useRef(0);

  const _QueryCustomerAccountReadVariables = React.useMemo(
    () => ({
      customerId: passOn?.customerId,
      skip: paginationModel.pageSize * paginationModel.page,
      take: paginationModel.pageSize,
      filter: _appliedFilter,
    }),
    [
      _appliedFilter,
      paginationModel.page,
      paginationModel.pageSize,
      passOn?.customerId,
    ],
  );

  const _QueryCustomerAccountRead = useQuery(CustomerAccountReadQuery, {
    variables: _QueryCustomerAccountReadVariables,
    fetchPolicy: _isPreviousPageRef.current
      ? "cache-first"
      : Object.React.App.fetchPolicy,
  });

  const { refetch } = _QueryCustomerAccountRead;

  const _HandleCustomerAccountDeleted = React.useCallback((ids = []) => {
    setDeletedCustomerAccountIds((current) =>
      Array.from(new Set([...current, ...ids].filter(Boolean))),
    );
  }, []);

  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  // Event handler.
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);
  const _HandlePaginationModelChange = React.useCallback(
    (__paginationModel) => {
      // Only backward pagination with the same page size should prefer cached rows.
      _isPreviousPageRef.current =
        __paginationModel.page < _paginationModelRef.current.page &&
        __paginationModel.pageSize === _paginationModelRef.current.pageSize;

      _paginationModelRef.current = __paginationModel;
      setPaginationModel(__paginationModel);
    },
    [],
  );
  const _HandleFilterModelChange = React.useCallback((__filterModel) => {
    _isPreviousPageRef.current = false;
    setPaginationModel((current) => {
      const nextPaginationModel = { ...current, page: 0 };

      _paginationModelRef.current = nextPaginationModel;

      return nextPaginationModel;
    });
    setFilterModel(__filterModel);
  }, []);

  // Group data by systemId
  const groupedData = React.useMemo(() => {
    const data = _QueryCustomerAccountRead?.data?.CustomerAccountRead;
    if (!data) return {};

    const deletedIds = new Set(deletedCustomerAccountIds);

    return data.reduce((groups, item) => {
      if (deletedIds.has(item?.id)) return groups;

      if (_successFlags.includes(item?.status) && item?.Smpp?.username) {
        const key = item.Smpp.username;
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      }
      return groups;
    }, {});
  }, [
    _QueryCustomerAccountRead?.data?.CustomerAccountRead,
    _successFlags,
    deletedCustomerAccountIds,
  ]);

  //  Toggle handler with no dependencies
  const handleToggleExpand = React.useCallback((groupKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  }, []);

  //  Send rates handlers
  const handleSendRates = React.useCallback(
    (itemId) => {
      setCustomerRateEmailInProgress((prev) => [...prev, itemId]);

      MutationRateEmail({ variables: { customerAccountIds: [itemId] } })
        .then((r) => toast(r?.data?.RatePlanEmail?.message))
        .catch((e) => toast.error(e?.message ?? "Something went wrong"))
        .finally(() => {
          setCustomerRateEmailInProgress((prev) =>
            prev.filter((id) => id !== itemId),
          );
        });
    },
    [MutationRateEmail],
  );

  const handleSendRatesMultiple = React.useCallback(
    (ids) => {
      setCustomerRateEmailInProgress((prev) => [...prev, ...ids]);

      MutationRateEmail({ variables: { customerAccountIds: ids } })
        .then((r) => toast(r?.data?.RatePlanEmail?.message))
        .catch((e) => toast.error(e?.message ?? "Something went wrong"))
        .finally(() => {
          setCustomerRateEmailInProgress((prev) =>
            prev.filter((id) => !ids.includes(id)),
          );
        });
    },
    [MutationRateEmail],
  );

  // DataGrid columns
  const columns = React.useMemo(
    () => [
      {
        field: "serialNo",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const shouldShowIcon =
            params.row.isGroup ||
            (params.row.hasMultiple &&
              params.row.isExpanded &&
              params.row.serialNo === "a");

          return (
            <Flex align="center" width="100%" position="relative">
              <Flex
                position="absolute"
                left="0px"
                width="45px"
                justify="center"
              >
                {shouldShowIcon && (
                  <IconButton
                    size="xs"
                    variant="ghost"
                    icon={
                      params.row.isGroup ? (
                        <ChevronRightIcon />
                      ) : (
                        <ChevronDownIcon />
                      )
                    }
                    onClick={() => handleToggleExpand(params.row.groupKey)}
                    aria-label={params.row.isGroup ? "Expand" : "Collapse"}
                    minW="20px"
                    h="20px"
                    _focus={{ boxShadow: "none" }}
                    _active={{ bg: "transparent" }}
                  />
                )}
              </Flex>
              <Flex width="100%" justify="center" align="center">
                {params.value}
              </Flex>
            </Flex>
          );
        },
      },
      {
        field: "id",
        headerName: "ID",
        hide: true,
      },
      {
        field: "displayName",
        headerName: "Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "Smpp.username",
        headerName: "System Id",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "Smpp.password",
        headerName: "Password",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "Smpp.ip",
        headerName: "IP",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 250,
      },
      {
        field: "Smpp.tps",
        headerName: "Smpp tps",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 100,
      },
      {
        field: "Smpp.sessionAllowed",
        headerName: "Session Allowed",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "Smpp.sessionTimeoutInterval",
        headerName: "Session ttl (in Seconds)",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "isActive",
        headerName: "Active",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <StatusBadge isActive={params.value} />
          </Box>
        ),
      },
      {
        field: "emailRates",
        headerName: "Email Rates",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const ids = params.row.isGroup
            ? params.row.allIds
            : [params.row.rawId];
          const isInProgress = params.row.isGroup
            ? params.row.allIds.some((id) =>
                customerRateEmailInProgress.includes(id),
              )
            : customerRateEmailInProgress.includes(params.row.rawId);

          return (
            <SendRatesButton
              isInProgress={isInProgress}
              onClick={() =>
                params.row.isGroup
                  ? handleSendRatesMultiple(ids)
                  : handleSendRates(params.row.rawId)
              }
            />
          );
        },
      },
      {
        field: "createdAt",
        headerName: "CreatedAt",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        disableColumnMenu: true,
        minWidth: 150,
      },
      {
        field: "updatedAt",
        headerName: "UpdatedAt",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        flex: 1,
        minWidth: 150,
      },
      {
        field: "createdBy",
        headerName: "Created By",
        description: "User who created the route.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        flex: 1,
        minWidth: 150,
      },
      {
        field: "actions",
        headerName: "Actions",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 100,
        renderCell: (params) => (
          <Flex align="center" justify="center" height="100%">
            <CustomerAccountDropDownOptions
              type={passOn?.type}
              mergedIPs={params.row.isGroup ? params.row.ip : params.row.ip}
              isMulti={params.row.isGroup || !params.row.hasMultiple}
              isExpandedIndividual={
                !params.row.isGroup &&
                params.row.hasMultiple &&
                params.row.isExpanded
              }
              customerAccountId={params.row.rawId}
              customerAccountIds={
                params.row.isGroup ? params.row.allIds : [params.row.rawId]
              }
              onCustomerBlockDuration={onCustomerBlockDurationOpen}
              onCustomerAccountUpdate={onCustomerAccountUpdateOpen}
              onCustomerAccountDelete={onCustomerAccountDeleteOpen}
            />
          </Flex>
        ),
      },
    ],
    [
      handleToggleExpand,
      handleSendRates,
      handleSendRatesMultiple,
      customerRateEmailInProgress,
      passOn?.type,
      onCustomerBlockDurationOpen,
      onCustomerAccountUpdateOpen,
      onCustomerAccountDeleteOpen,
    ],
  );

  // Rows calculation
  const rows = React.useMemo(() => {
    const result = [];
    const entries = Object.entries(groupedData);

    entries.forEach(([systemId, items], groupIndex) => {
      const isExpanded = expandedRows[systemId];
      const hasMultiple = items.length > 1;

      if (!isExpanded && hasMultiple) {
        // Collapsed group row
        const firstItem = items[0];
        const allIPs =
          items
            .map((i) => i?.Smpp?.ip)
            .filter(Boolean)
            .join(", ") || "-";
        const allIds = items.map((i) => i.id);

        result.push({
          id: `group-${systemId}`,
          rawId: firstItem?.id,
          serialNo: `${groupIndex + 1}`,
          displayName: firstItem?.Smpp?.displayName ?? "-",
          "Smpp.username": firstItem?.Smpp?.username ?? "-",
          "Smpp.password": firstItem?.Smpp?.password ?? "-",
          "Smpp.ip": allIPs,
          "Smpp.tps": firstItem?.Smpp?.tps ?? "-",
          "Smpp.sessionAllowed": firstItem?.Smpp?.sessionAllowed ?? "-",
          "Smpp.sessionTimeoutInterval": firstItem?.Smpp?.sessionTimeoutInterval
            ? firstItem.Smpp.sessionTimeoutInterval / 1000
            : (firstItem?.Smpp?.sessionTimeoutInterval ?? "-"),
          isActive: firstItem?.Smpp?.isActive,
          createdAt: Moment(firstItem?.Smpp?.createdAt).format("DD-MM-YYYY"),
          updatedAt: Moment(firstItem?.Smpp?.updatedAt).format("DD-MM-YYYY"),
          createdBy: firstItem?.Account?.displayName || "-",
          isGroup: true,
          isExpanded: false,
          hasMultiple: true,
          groupKey: systemId,
          allIds: allIds,
        });
      } else {
        // Expanded individual rows
        items.forEach((item, idx) => {
          result.push({
            id:
              item?.id ??
              `customer-account-${paginationModel.page}-${paginationModel.pageSize}-${groupIndex}-${idx}`,
            rawId: item?.id,
            serialNo: hasMultiple
              ? String.fromCharCode(97 + idx)
              : `${groupIndex + 1}`,
            displayName: item?.Smpp?.displayName ?? "-",
            "Smpp.username": item?.Smpp?.username ?? "-",
            "Smpp.password": item?.Smpp?.password ?? "-",
            "Smpp.ip": item?.Smpp?.ip ?? "-",
            "Smpp.tps": item?.Smpp?.tps ?? "-",
            "Smpp.sessionAllowed": item?.Smpp?.sessionAllowed ?? "-",
            "Smpp.sessionTimeoutInterval": item?.Smpp?.sessionTimeoutInterval
              ? item.Smpp.sessionTimeoutInterval / 1000
              : (item?.Smpp?.sessionTimeoutInterval ?? "-"),
            isActive: item?.Smpp?.isActive,
            createdAt: Moment(item?.Smpp?.createdAt).format("DD-MM-YYYY"),
            updatedAt: Moment(item?.Smpp?.updatedAt).format("DD-MM-YYYY"),
            createdBy: item?.Account?.displayName || "-",
            isGroup: false,
            isExpanded: true,
            hasMultiple,
            groupKey: systemId,
          });
        });
      }
    });

    return result;
  }, [groupedData, expandedRows]);

  // Total count
  const _QueryCustomerAccountReadRowCount = _.first(
    _QueryCustomerAccountRead?.data?.CustomerAccountRead,
  )?._totalCount;
  const totalCount = React.useMemo(() => {
    if (
      undefined !== _QueryCustomerAccountReadRowCount &&
      null !== _QueryCustomerAccountReadRowCount
    )
      _rowCountRef.current = _QueryCustomerAccountReadRowCount;

    return _rowCountRef.current;
  }, [_QueryCustomerAccountReadRowCount]);
  const _downloadCellsData = React.useMemo(
    () =>
      _QueryCustomerAccountRead?.data?.CustomerAccountRead?.map(
        (item, __index) =>
          _successFlags.includes(item.status) && item?.Smpp
            ? {
                "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                Name: item?.Smpp?.displayName ?? "-",
                "System Id": item?.Smpp?.username ?? "-",
                Password: item?.Smpp?.password ?? "-",
                Ip: item?.Smpp?.ip ?? "-",
                "Smpp tps": item?.Smpp?.tps ?? "-",
                "Session Allowed": item?.Smpp?.sessionAllowed ?? "-",
                "Session ttl ( in Seconds )":
                  (0 < item?.Smpp?.sessionTimeoutInterval
                    ? item?.Smpp?.sessionTimeoutInterval / 1000
                    : item?.Smpp?.sessionTimeoutInterval) ?? "-",
                Active: item?.Smpp?.isActive ? "Active" : "Inactive",
                CreatedAt: Moment(item?.Smpp?.createdAt).format("DD-MM-YYYY"),
                UpdatedAt: Moment(item?.Smpp?.updatedAt).format("DD-MM-YYYY"),
              }
            : {},
      ),
    [
      _QueryCustomerAccountRead?.data?.CustomerAccountRead,
      _successFlags,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );
  const _downloadHeadersData = React.useMemo(
    () =>
      [
        "S.No.",
        "Name",
        "System Id",
        "Password",
        "Ip",
        "Smpp tps",
        "Session Allowed",
        "Session ttl ( in Seconds )",
        "Active",
        "CreatedAt",
        "UpdatedAt",
      ].map((i) => ({ key: i, label: i })),
    [],
  );

  // Return component
  return (
    <>
      <Flex className="customerAccount base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          position="relative"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          minH="81vh"
          maxH="81vh"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex
            pb="0px"
            justify="space-between"
            gap={2}
            flexWrap={{ base: "nowrap", md: "nowrap" }}
          >
            <Tooltip label="Create new smpp." fontSize="sm">
              <Button
                onClick={onCustomerAccountCreateOpen}
                leftIcon={<IoMdAdd />}
                style={buttonStyle}
              >
                Create Account
              </Button>
            </Tooltip>
            <Flex gap="12px">
              <Button
                borderRadius="8px"
                fontWeight="450"
                minW="20px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
              <DownloadToExcel
                cellsData={_downloadCellsData}
                headersData={_downloadHeadersData}
              />
            </Flex>
          </Flex>

          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={totalCount}
            paginationMode="server"
            filterMode="server"
            sortMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            filterModel={filterModel}
            onFilterModelChange={_HandleFilterModelChange}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            pageSizeOptions={[30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            loading={_QueryCustomerAccountRead?.loading}
          />
        </Flex>

        {/* Modals */}
        <Modal
          size="2xl"
          title={
            _isCurrentViewMobile ? "Customer AC" : "Customer Account Create"
          }
          isOpen={isCustomerAccountCreateOpen}
          onClose={onCustomerAccountCreateClose}
          isCentered={false}
        >
          <CustomerAccountUpsert
            isCreateOnly={true}
            isOpen={isCustomerAccountCreateOpen}
            onClose={onCustomerAccountCreateClose}
            refetch={refetch}
          />
        </Modal>
        <Modal
          size="2xl"
          title={
            _isCurrentViewMobile ? "Customer AU" : "Customer Account Update"
          }
          isOpen={isCustomerAccountUpdateOpen}
          onClose={onCustomerAccountUpdateClose}
          isCentered={false}
        >
          <CustomerAccountUpsert
            isOpen={isCustomerAccountUpdateOpen}
            onClose={onCustomerAccountUpdateClose}
            refetch={refetch}
          />
        </Modal>
        <Modal
          size="sm"
          title={
            _isCurrentViewMobile ? "Customer AD" : "Customer Account Delete"
          }
          isOpen={isCustomerAccountDeleteOpen}
          onClose={onCustomerAccountDeleteClose}
        >
          <CustomerAccountDelete
            onClose={onCustomerAccountDeleteClose}
            onDeleted={_HandleCustomerAccountDeleted}
            refetch={refetch}
          />
        </Modal>
        <Modal
          size="lg"
          title="Block Traffic"
          isOpen={isCustomerBlockDurationOpen}
          onClose={onCustomerBlockDurationClose}
          isCentered={true}
        >
          <CustomerBlockBetweenDuration showingFor="CUSTOMER" />
        </Modal>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {
  passOn: PropTypes.object,
};

/*
 * REDUX
 */
const _MapStateToProps = (__state) => ({ passOn: __state.PassOn });

/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index);
